/**
 * Transport Icons for Route Timeline
 * 
 * Inline SVGs based on Lucide icon style.
 * No external dependencies required.
 */

import { type TransportType } from '../../services/api';

interface IconProps {
    size?: number;
    className?: string;
    strokeWidth?: number;
}

const defaultProps = {
    size: 20,
    strokeWidth: 2,
};

/**
 * Plane icon for flights
 */
export function PlaneIcon({ size = defaultProps.size, className, strokeWidth = defaultProps.strokeWidth }: IconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" />
        </svg>
    );
}

/**
 * Train icon for rail travel
 */
export function TrainIcon({ size = defaultProps.size, className, strokeWidth = defaultProps.strokeWidth }: IconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            <rect width="16" height="16" x="4" y="3" rx="2" />
            <path d="M4 11h16" />
            <path d="M12 3v8" />
            <path d="m8 19-2 3" />
            <path d="m18 22-2-3" />
            <path d="M8 15h0" />
            <path d="M16 15h0" />
        </svg>
    );
}

/**
 * Car icon for driving/rental
 */
export function CarIcon({ size = defaultProps.size, className, strokeWidth = defaultProps.strokeWidth }: IconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" />
            <circle cx="7" cy="17" r="2" />
            <path d="M9 17h6" />
            <circle cx="17" cy="17" r="2" />
        </svg>
    );
}

/**
 * Bus icon for bus travel
 */
export function BusIcon({ size = defaultProps.size, className, strokeWidth = defaultProps.strokeWidth }: IconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            <path d="M8 6v6" />
            <path d="M15 6v6" />
            <path d="M2 12h19.6" />
            <path d="M18 18h3s.5-1.7.8-2.8c.1-.4.2-.8.2-1.2 0-.4-.1-.8-.2-1.2l-1.4-5C20.1 6.8 19.1 6 18 6H4a2 2 0 0 0-2 2v10h3" />
            <circle cx="7" cy="18" r="2" />
            <path d="M9 18h5" />
            <circle cx="16" cy="18" r="2" />
        </svg>
    );
}

/**
 * Ferry/Ship icon for water travel
 */
export function FerryIcon({ size = defaultProps.size, className, strokeWidth = defaultProps.strokeWidth }: IconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            <path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1 .6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" />
            <path d="M19.38 20A11.6 11.6 0 0 0 21 14l-9-4-9 4c0 2.9.94 5.34 2.81 7.76" />
            <path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6" />
            <path d="M12 10v4" />
            <path d="M12 2v3" />
        </svg>
    );
}

/**
 * Map Pin icon for origin/destination points
 */
export function MapPinIcon({ size = defaultProps.size, className, strokeWidth = defaultProps.strokeWidth }: IconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
            <circle cx="12" cy="10" r="3" />
        </svg>
    );
}

/**
 * Circle icon for unknown transport
 */
export function CircleIcon({ size = defaultProps.size, className, strokeWidth = defaultProps.strokeWidth }: IconProps) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            <circle cx="12" cy="12" r="10" />
        </svg>
    );
}

/**
 * Get the appropriate transport icon component for a transport type
 */
export function getTransportIcon(type: TransportType): React.FC<IconProps> {
    const iconMap: Record<TransportType, React.FC<IconProps>> = {
        flight: PlaneIcon,
        train: TrainIcon,
        car: CarIcon,
        bus: BusIcon,
        ferry: FerryIcon,
        unknown: CircleIcon,
    };
    return iconMap[type];
}

/**
 * Render transport icon by type
 */
export function TransportIcon({ type, ...props }: IconProps & { type: TransportType }) {
    const Icon = getTransportIcon(type);
    return <Icon {...props} />;
}

// Export all icons
export const TransportIcons = {
    PlaneIcon,
    TrainIcon,
    CarIcon,
    BusIcon,
    FerryIcon,
    MapPinIcon,
    CircleIcon,
    TransportIcon,
    getTransportIcon,
};

export default TransportIcons;

